import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, KeyRound, Lock, ShieldCheck, Unlock, AlertCircle } from "lucide-react";

const TOKEN_KEY_PREFIX = "cc_vault_token__";

// iter-64: Playground rolls up under Licensing — same passcode unlocks both.
// iter-74: Billing removed — it has its OWN dedicated passcode.
// iter-81ac: One shared passcode covers Licensing File, Staffs File, Drill
// Log, Playground, Food Menu, Staff App, and Substitutes. Unlocking any one
// unlocks them all.
// iter-81ao: No auto-expiry. Vault stays unlocked from initial passcode entry
// until the owner signs out (localStorage-persisted; cleared on logout).
// Compliance is NOT vault-locked.
const SECTIONS = [
  { prefix: "/dashboard/records",              section: "records",    label: "Records" },
  { prefix: "/dashboard/staff-files",          section: "licensing",  label: "Staffs File" },
  { prefix: "/dashboard/staff/documents",      section: "licensing",  label: "Staffs File" },
  { prefix: "/dashboard/staff/training",       section: "licensing",  label: "Staffs File" },
  { prefix: "/dashboard/staff/substitutes",    section: "licensing",  label: "Substitutes" },
  { prefix: "/dashboard/staff",                section: "licensing",  label: "Staff App" },
  { prefix: "/dashboard/licensing/food-menu",  section: "licensing",  label: "Food Menu" },
  { prefix: "/dashboard/licensing",            section: "licensing",  label: "Licensing File" },
  { prefix: "/dashboard/playground",           section: "licensing",  label: "Playground" },
  { prefix: "/dashboard/drill-log",            section: "licensing",  label: "Drill Log" },
];

export default function VaultLock() {
  const location = useLocation();
  const match = SECTIONS.find(({ prefix }) => location.pathname.startsWith(prefix));
  const section = match?.section || "vault";
  const sectionName = match?.label || "Sensitive area";
  const TOKEN_KEY = `${TOKEN_KEY_PREFIX}${section}`;

  const [status, setStatus] = useState("loading");
  const [passcode, setPasscode] = useState("");
  const [confirmPasscode, setConfirmPasscode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setStatus("loading");
    const cached = localStorage.getItem(TOKEN_KEY);
    api.get("/vault/passcode/status", { params: { section } })
      .then((r) => {
        const isSet = !!r.data?.is_set;
        if (!isSet) setStatus("setup");
        else if (cached) setStatus("unlocked");
        else setStatus("locked");
      })
      .catch((err) => {
        if (err.response?.status === 403) setStatus("forbidden");
        else { toast.error(formatApiError(err)); setStatus("locked"); }
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  // iter-81an: relock event from axios interceptor when a real vault 401 fires.
  useEffect(() => {
    const onRelock = (evt) => {
      const evtSection = evt?.detail?.section;
      if (!evtSection || evtSection !== section) return;
      setStatus((s) => (s === "unlocked" ? "locked" : s));
    };
    window.addEventListener("cc-vault-relock", onRelock);
    return () => window.removeEventListener("cc-vault-relock", onRelock);
  }, [section]);

  const setupPasscode = async (e) => {
    e.preventDefault();
    if (passcode.trim().length < 4) return toast.error("Passcode must be at least 4 characters.");
    if (passcode !== confirmPasscode) return toast.error("Passcodes don't match.");
    setBusy(true);
    try {
      const r = await api.post("/vault/passcode/set", { passcode, section });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      setStatus("unlocked");
      toast.success(`${sectionName} passcode set — you're in.`);
      setPasscode(""); setConfirmPasscode("");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const unlock = async (e) => {
    e.preventDefault();
    if (!passcode.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/vault/passcode/verify", { passcode, section });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      setStatus("unlocked");
      setPasscode("");
      toast.success(`${sectionName} unlocked.`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const requestReset = async () => {
    setBusy(true);
    try {
      const r = await api.post("/vault/passcode/forgot", { section });
      toast.success(`${sectionName} reset link sent to ${r.data.sent_to}. Check your email — it expires in 15 min.`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (status === "loading") {
    return <div className="py-20 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700" /></div>;
  }

  if (status === "forbidden") {
    return (
      <Shell title="Owner-only area" icon={Lock} tone="amber"
        description={`${sectionName} is locked to the center owner. If you need access, ask your owner to log in.`}>
        <div className="text-sm text-stone-500">You don't have owner permission on this center.</div>
      </Shell>
    );
  }

  if (status === "setup") {
    return (
      <Shell title="Set a vault passcode" icon={KeyRound}
        description={`${sectionName} stores sensitive employee/center records. Choose a 4–32 character passcode. You'll only enter it once and stay unlocked until you sign out.`}>
        <form onSubmit={setupPasscode} className="space-y-3" data-testid="vault-setup-form">
          <PasscodeInput label="New passcode" value={passcode} onChange={setPasscode} testId="vault-set-passcode" autoFocus />
          <PasscodeInput label="Confirm passcode" value={confirmPasscode} onChange={setConfirmPasscode} testId="vault-confirm-passcode" />
          <Button type="submit" disabled={busy} data-testid="vault-set-submit"
            className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                  : <><ShieldCheck className="w-4 h-4 mr-2" /> Set passcode & unlock</>}
          </Button>
        </form>
        <p className="mt-4 text-[11px] text-stone-500">
          One passcode unlocks the whole sensitive cluster: Licensing File, Staffs File, Drill Log, Playground, Food Menu, Staff App, and Substitutes. Billing has its own separate passcode (set on the Billing page).
        </p>
      </Shell>
    );
  }

  if (status === "locked") {
    return (
      <Shell title={`${sectionName} is locked`} icon={Lock}
        description={`Enter your owner vault passcode to unlock ${sectionName}. You'll stay unlocked until you sign out.`}>
        <form onSubmit={unlock} className="space-y-3" data-testid="vault-unlock-form">
          <PasscodeInput label="Vault passcode" value={passcode} onChange={setPasscode} testId="vault-unlock-passcode" autoFocus />
          <Button type="submit" disabled={busy} data-testid="vault-unlock-submit"
            className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying…</>
                  : <><Unlock className="w-4 h-4 mr-2" /> Unlock</>}
          </Button>
        </form>
        <div className="mt-5 pt-4 border-t border-stone-200">
          <button type="button" data-testid="vault-forgot-link"
            onClick={requestReset}
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm font-semibold hover:bg-emerald-100 hover:border-emerald-300 disabled:opacity-50 transition-colors">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            Forgot your passcode? Email me a reset link
          </button>
          <p className="mt-2.5 text-[11px] text-stone-500 text-center leading-relaxed">
            We'll email a one-time link to the owner address on file. Expires in 15 minutes.
          </p>
        </div>
      </Shell>
    );
  }

  return <Outlet />;
}

function Shell({ title, description, icon: Icon, children, tone = "emerald" }) {
  const accent = tone === "amber" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";
  return (
    <div className="min-h-[60vh] grid place-items-center px-4" data-testid="vault-lock-shell">
      <div className="w-full max-w-md rounded-2xl border border-stone-200 bg-white shadow-sm p-7">
        <div className={`w-12 h-12 rounded-xl ${accent} grid place-items-center mb-5`}>
          <Icon className="w-6 h-6" />
        </div>
        <h1 className="font-display text-2xl font-medium text-stone-900">{title}</h1>
        <p className="mt-2 text-sm text-stone-600 leading-relaxed">{description}</p>
        <div className="mt-6">{children}</div>
        <div className="mt-6 flex items-center gap-2 text-[11px] text-stone-400">
          <AlertCircle className="w-3.5 h-3.5" /> Owner-only · Stays unlocked until sign-out
        </div>
      </div>
    </div>
  );
}

function PasscodeInput({ label, value, onChange, testId, autoFocus }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider text-stone-700">{label}</Label>
      <Input type="password" inputMode="numeric" autoComplete="off" autoFocus={autoFocus}
        data-testid={testId}
        value={value} onChange={(e) => onChange(e.target.value)}
        className="h-11 rounded-xl border-stone-300 focus-visible:ring-emerald-700 text-base tracking-widest" />
    </div>
  );
}
